import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  DoCheck,
  inject,
  input,
  signal,
  TemplateRef
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControlStatus } from '@angular/forms';
import { map, Observable, of, startWith, switchMap } from 'rxjs';
import { StepContentDirective } from '../../directives/step-content.directive';
import { StepFooterDirective } from '../../directives/step-footer.directive';

@Component({
  selector: 'osp-step',
  standalone: true,
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepComponent implements AfterContentInit, DoCheck {
  private destroyRef = inject(DestroyRef);

  @ContentChild(StepContentDirective) stepContent!: StepContentDirective;
  @ContentChild(StepFooterDirective) stepFooter!: StepFooterDirective;

  title = input<string>('');
  icon = input<string | null>(null);
  control = input<AbstractControl | null | undefined>(null);

  contentTemplate = signal<TemplateRef<unknown> | null>(null);
  footerTemplate = signal<TemplateRef<unknown> | null>(null);
  isValid = signal(true);

  constructor() {
    toObservable(this.control)
      .pipe(
        switchMap((control) => this.getControlStateObs(control)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isValid) => {
        this.isValid.set(isValid)
      });
  }

  ngDoCheck(): void {
    this.isValid.set(Boolean(this.control()?.valid));
  }

  getControlStateObs(control: AbstractControl | null | undefined): Observable<boolean> {
    if (!control) {
      return of(true);
    }

    // Handle async validation completion for controls in PENDING state
    // Angular's statusChanges observable may not emit when async validation completes
    // if the status transition happens before the subscription is fully established.
    // This polling mechanism ensures we capture the final status after async validation.
    if (control.pending) {
      const checkPending = () => {
        if (control.pending) {
          // Continue polling every 100ms until async validation completes
          setTimeout(checkPending, 100);
        } else {
          // Async validation completed - force a status update to trigger statusChanges emission
          // This ensures the observable emits the final VALID/INVALID status
          control.updateValueAndValidity({ onlySelf: true });
        }
      };
      checkPending();
    }

    return control.statusChanges.pipe(
      startWith(control.status),
      map((state: FormControlStatus) => state === 'VALID'),
    );
  }

  ngAfterContentInit(): void {
    this.contentTemplate.set(this.stepContent?.template || null);
    this.footerTemplate.set(this.stepFooter?.template || null);
  }
}
